import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { admin, User } from '../lib/api'

export default function AdminPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Always attempt to load — the API will return 401/403 for non-admins
    // and we'll show a helpful auth error instead of empty tables
    loadUsers()
  }, [user])

  const loadUsers = async () => {
    try {
      const data = await admin.getUsers()
      setUsers(data)
      setError(null)
    } catch (e: any) {
      const status = e?.status
      if (status === 401 || status === 403 || e?.message?.includes('Admin access')) {
        setError('auth')
      } else {
        setError('unknown')
      }
      console.error('Error loading users:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleAdjustBalance = async (userId: string, amount: number) => {
    try {
      await admin.adjustBalance(userId, amount)
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === userId
            ? { ...u, api_balance: Math.max(0, u.api_balance + amount) }
            : u
        )
      )
    } catch (e) {
      console.error('Error adjusting balance:', e)
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString()

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
          <p className="mt-3 text-gray-400 text-sm">Loading admin data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 max-w-7xl mx-auto w-full">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-100">Admin Panel</h1>
        <button
          onClick={() => navigate('/admin/agent')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Agent Dashboard
        </button>
      </div>

      {/* User table */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50 border-b border-gray-800">
              <tr>
                {['Email', 'Balance', 'Role', 'Status', 'Joined', 'Actions'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {users.map((u) => (
                <tr key={u.user_id} className="hover:bg-gray-800/30 transition">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => navigate(`/profile/${u.user_id}`)}
                      className="text-brand-400 hover:text-brand-300 hover:underline transition"
                      title="View profile"
                    >
                      {u.email}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">
                    ${u.api_balance?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        u.role === 'admin'
                          ? 'bg-purple-900/40 text-purple-300'
                          : 'bg-gray-800 text-gray-400'
                      }`}
                    >
                      {u.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        u.approval_status === 'approved'
                          ? 'bg-green-900/40 text-green-400'
                          : 'bg-yellow-900/40 text-yellow-400'
                      }`}
                    >
                      {u.approval_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(u.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleAdjustBalance(u.user_id, -1)}
                        className="p-1.5 text-red-400 hover:bg-red-900/20 rounded transition"
                        title="Decrease balance by $1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleAdjustBalance(u.user_id, 1)}
                        className="p-1.5 text-green-400 hover:bg-green-900/20 rounded transition"
                        title="Increase balance by $1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {error === 'auth' ? (
              <div>
                <svg className="w-8 h-8 mx-auto mb-3 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m9.364-7.364A9 9 0 116.636 6.636 9 9 0 0119.364 16.636z" />
                </svg>
                <p className="text-amber-400 font-medium">Authentication Required</p>
                <p className="text-gray-500 text-sm mt-1">Sign in with an admin account to manage users.</p>
              </div>
            ) : (
              <p>No users found</p>
            )}
          </div>
        )}
      </div>

      {/* Stats — hidden when not authenticated */}
      {error !== 'auth' && <div className="mt-6 bg-brand-950/30 border border-brand-800/30 rounded-lg p-5">
        <h3 className="font-semibold text-brand-200 mb-3">Usage Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-brand-300 text-sm">Total Users</p>
            <p className="text-2xl font-bold text-brand-100">{users.length}</p>
          </div>
          <div>
            <p className="text-brand-300 text-sm">Total Credits</p>
            <p className="text-2xl font-bold text-brand-100">
              ${users.reduce((sum, u) => sum + (u.api_balance || 0), 0).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-brand-300 text-sm">Admin Users</p>
            <p className="text-2xl font-bold text-brand-100">
              {users.filter((u) => u.role === 'admin').length}
            </p>
          </div>
        </div>
      </div>}
    </div>
  )
}
